import React from 'react';
import { SEO } from '../components/SEO';
import { Container } from '../components/ui/Container';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { BookOpen, FileText, Download, Rocket, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ResourcesPage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Resources & Guides - Flowsense',
    description: 'Comprehensive guides, tutorials, and resources for mastering Klaviyo flow optimization with Flowsense',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Getting Started with Flowsense',
        description: 'Complete guide to setting up Flowsense and running your first flow analysis'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Understanding Flow Health Scores',
        description: 'Learn how Flowsense calculates health scores and metrics'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'Implementing AI Recommendations',
        description: 'Best practices for prioritizing and implementing recommendations'
      }]
    }
  };
  const guides = [{
    title: 'Help Center & Knowledge Base',
    description: 'Search our comprehensive database of articles, tutorials, and FAQs to find answers quickly.',
    icon: HelpCircle,
    category: 'Support',
    readTime: 'Searchable',
    slug: '/help'
  }, {
    title: 'Getting Started with Flowsense',
    description: 'Your complete guide to setting up Flowsense and running your first flow analysis in under 5 minutes.',
    icon: Rocket,
    category: 'Guide',
    readTime: '5 min read',
    slug: '/guides/getting-started'
  }, {
    title: 'Understanding Flow Health Scores',
    description: 'Learn how Flowsense calculates health scores and what each metric means for your email marketing performance.',
    icon: BookOpen,
    category: 'Guide',
    readTime: '8 min read',
    slug: '/guides/understanding-flow-health-scores'
  }, {
    title: 'Implementing AI Recommendations',
    description: 'Best practices for prioritizing and implementing recommendations to maximize ROI and improve flow performance.',
    icon: FileText,
    category: 'Guide',
    readTime: '10 min read',
    slug: '/guides/implementing-ai-recommendations'
  }, {
    title: 'Agency White-Label Setup',
    description: 'Complete guide to setting up custom domains and branded reports for your agency clients with white-label features.',
    icon: BookOpen,
    category: 'Guide',
    readTime: '12 min read',
    slug: '/guides/agency-white-label-setup'
  }, {
    title: 'Flow Optimization Playbook',
    description: 'Proven strategies for optimizing Welcome, Abandonment, and Post-Purchase flows with real-world examples.',
    icon: Download,
    category: 'Template',
    readTime: 'PDF Download',
    slug: '#'
  }, {
    title: 'ROI Reporting Template',
    description: 'Ready-to-use spreadsheet template for tracking and reporting flow optimization ROI to stakeholders.',
    icon: Download,
    category: 'Template',
    readTime: 'Excel Download',
    slug: '#'
  }];
  return <>
      <SEO title="Resources & Guides - Flowsense" description="Comprehensive guides, tutorials, and resources for mastering Klaviyo flow optimization. Learn best practices, implementation strategies, and advanced techniques." canonical="https://www.useflowsense.com/resources" structuredData={structuredData} data-id="element-2127" />
      <div className="bg-white" data-id="element-2128">
        {/* Hero */}
        <div className="bg-gray-900 py-20 lg:py-32 text-center text-white" data-id="element-2129">
          <Container data-id="element-2130">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6" data-id="element-2131">
              Resources & Guides
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-2132">
              Everything you need to master flow optimization and maximize your
              Klaviyo ROI.
            </p>
          </Container>
        </div>

        {/* Guides Grid */}
        <section className="py-24" data-id="element-2133">
          <Container data-id="element-2134">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2135">
              {guides.map((guide, index) => <Card key={index} className="hover:shadow-lg transition-shadow flex flex-col" data-id="element-2136">
                  <CardContent className="p-6 flex flex-col flex-grow" data-id="element-2137">
                    <div className="flex items-center justify-between mb-4" data-id="element-2138">
                      <div className="p-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-lg" data-id="element-2139">
                        <guide.icon className="h-6 w-6 text-white" data-id="element-2140" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-2141">
                        {guide.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2142">
                      {guide.title}
                    </h3>
                    <p className="text-gray-600 mb-4 flex-grow" data-id="element-2143">
                      {guide.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto" data-id="element-2144">
                      <span className="text-sm text-gray-500" data-id="element-2145">
                        {guide.readTime}
                      </span>
                      {guide.slug !== '#' ? <Link to={guide.slug} data-id="element-2146">
                          <Button variant="link" className="p-0 h-auto" data-id="element-2147">
                            Read More →
                          </Button>
                        </Link> : <Button variant="link" className="p-0 h-auto opacity-50 cursor-not-allowed" data-id="element-2148">
                          Coming Soon
                        </Button>}
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gray-900" data-id="element-2149">
          <Container data-id="element-2150">
            <div className="text-center max-w-3xl mx-auto" data-id="element-2151">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6" data-id="element-2152">
                Ready to Start Optimizing?
              </h2>
              <p className="text-xl text-gray-300 mb-10" data-id="element-2153">
                Put these guides into action with Flowsense.
              </p>
              <a href="https://go.useflowsense.com/signup" data-id="element-2154">
                <Button size="lg" data-id="element-2155">Sign Up</Button>
              </a>
            </div>
          </Container>
        </section>
      </div>
    </>;
}